import React, { useContext, useEffect, useRef, useState } from 'react'
import { DOCUMENT_HELPER, printReceiptPrepareData, route, toastUtils } from '../../utils'
import { FiscalDeviceAPI } from '../../printerService'
import { useTranslation } from 'react-i18next'
import { AppCtx } from '../providers/AppProvider.jsx'
import { PowerIcon, PowerOff, Printer, SignalIcon, ZapIcon, ZapOffIcon } from 'lucide-react'
import { Button } from 'devextreme-react/button'
import { renderToStaticMarkup } from 'react-dom/server'
import axios from 'axios'
import links from '../../links.json'
import { Col, Row } from '../Grid/index.jsx'
import { Modal } from '../Shared/Modal.jsx'

const fiscalDeviceApi = new FiscalDeviceAPI({
  printerPortKey: 'fiscalPrinterPort'
})

export const useFiscalDevice = () => {
  const { t } = useTranslation()
  const modalRef = useRef(null)
  const { startLoading, endLoading } = useContext(AppCtx)

  const [isConnected, setIsConnected] = useState(fiscalDeviceApi.isSerialPortOpen())

  const fiscalDevice = {
    autoReconnect: () => {
      if (!fiscalDeviceApi.isSerialPortOpen() && !isConnected) {
        fiscalDeviceApi.getSavedSerialPort()
          .then(res => {
            if (res) {
              setIsConnected(true)
            }
          })
      }
    },
    isConnected,
    setIsConnected,
    api: fiscalDeviceApi,
    handleResponse (res, config = {}) {
      if (res.success) {
        setIsConnected(true)

        if (res.data.errors?.length) {
          toastUtils.error(res.data.errors.join(', '))
        } else {
          toastUtils.success(`${t('labels.fiscal_printer')} - ${t('messages.processed_successfully')}`)
          config.successCallback && config.successCallback()
        }
      }
      !res.success && toastUtils.error(res.data?.message)
    },
    async handlePrintReceipt (document, config = {}) {
      startLoading()

      const res = await printReceiptPrepareData(document)
        .then(async res => {
          if (res.data.order) {
            return await fiscalDeviceApi.createSale(res.data.order)
          }
        })

      this.handleResponse(res, {
        successCallback: () => {
          axios.post(route(links.documents.handleFiscalPrinted, {
            documentTypeBind: DOCUMENT_HELPER.getDocumentTypeLink(document.type),
            documentHeader: document.id
          }))
            .then(() => {
              config.successCallback && config.successCallback()
            })
            .catch(function (error) {
              if (error.response?.data?.message) {
                toastUtils.error(error.response?.data?.message)
              }
            })
        }
      })

      endLoading()
    },
    printConnection () {
      return (
        <>
          <span>
            {
              this.isConnected
                ? <PowerIcon size={14} className='inline-block' color='#10b981' />
                : <PowerOff size={14} className='inline-block' color='#d12f0f' />
            }
          </span>
          <span className='ml-2'>
            {t('labels.fiscal_printer')}
          </span>

          <Button
            className='ml-4'
            icon={renderToStaticMarkup(<SignalIcon size={14} />)}
            text={t('labels.connection')}
            stylingMode='outlined'
            onClick={() => {
              modalRef.current.open()
              modalRef.current.setInnerData({
                title: t('labels.fiscal_printer'),
                body: (
                  <FiscalDeviceModal
                    isConnected={isConnected}
                    setIsConnected={setIsConnected}
                    fiscalDevice={this}
                  />
                ),
                modalProps: {
                  width: 600,
                  hideConfirmButton: true
                }
              })
            }}
          />

          <Modal ref={modalRef} />
        </>
      )
    }
  }

  useEffect(() => {
    fiscalDevice.autoReconnect()
  }, [])

  return { fiscalDevice }
}

const FiscalDeviceModal = ({
  isConnected,
  setIsConnected,
  fiscalDevice
}) => {
  const { t } = useTranslation()

  const [isConnectedLocal, setIsConnectedLocal] = useState(isConnected)

  useEffect(() => {
    setIsConnected(isConnectedLocal)
  }, [isConnectedLocal])

  return (
    <Row>
      <Col>
        <Button
          icon={renderToStaticMarkup(
            !isConnectedLocal
              ? <ZapIcon size={14} />
              : <ZapOffIcon size={14} />
          )}
          text={!isConnectedLocal ? t('ui.connect') : t('ui.disconnect')}
          stylingMode='outlined'
          onClick={() => {
            if (!fiscalDevice.api.isSerialPortOpen() && !isConnectedLocal) {
              fiscalDevice.api.connect()
                .then(() => {
                  setIsConnectedLocal(true)
                })
                .catch(error => {
                  toastUtils.error(error)
                })
            } else {
              fiscalDevice.api.disconnect({ deleteSavedConnection: true })
              setIsConnectedLocal(false)
            }
          }}
        />
        <Button
          className='ml-4'
          icon={renderToStaticMarkup(<Printer size={14} />)}
          text={`${t('ui.test_print')} - ${t('ui.control_report')}`}
          stylingMode='outlined'
          onClick={async () => {
            const res = await fiscalDeviceApi.runDailyFinancialReport(false)
            fiscalDevice.handleResponse(res)
          }}
          disabled={!isConnectedLocal}
        />
      </Col>
    </Row>
  )
}
